import {Box} from '@mui/material';
import {FC, useEffect, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {LocationStatisticType} from './LocationStatistic.types';
import {MapZoomEnum} from 'src/utils/consts';
import theme from 'src/theme';
import {MapWrapper} from 'src/components/map/MapWrapper';
import {locationsApi} from '../../../api';
import {useBusinessId} from 'src/app/hooks';
import {AppRouteEnum, AppRouteSpace} from 'src/utils/helper';

/**
 * The map, full width, above the block that counts what is on it.
 *
 * It shared the row with two KPI cards until the counts became a block of their own: the map answers
 * where, the block answers what and how many, and stacking them lets each have the width it needs.
 *
 * A height rather than a minimum, so nothing below moves as the pins land.
 */
const ROW_HEIGHT = 300;

const rowSx = {
    // Clears the app bar: this card is the first thing on the page now that its header is gone.
    mt: 2.5,
};

const mapCardSx = {
    background: theme.palette.common.white,
    borderRadius: '14px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
    p: 2,
    height: ROW_HEIGHT,
};

const mapTileSx = {
    height: '100%',
    minHeight: `${ROW_HEIGHT - 32}px`,
    borderRadius: '10px',
    border: `1px solid ${theme.palette.neutral[200]}`,
    overflow: 'hidden',
    '& > div': {height: '100%'},
};

export const LocationStatistic: FC<LocationStatisticType> = ({filterParams}) => {
    const businessId = useBusinessId();
    const intl = useIntl();
    const [getMapPoints, mapPoints] = locationsApi.useLazyGetLocationsForMapQuery();

    // The filters, plus the business being worked in when they do not name one. Sending nothing was
    // right for everyone whose reach the server clamps - and wrong for the one caller it does not:
    // a platform administrator reaches every tenant, so the map and the counts beside it showed the
    // whole platform and went on showing it after the business was switched.
    useEffect(() => {
        const data = Object.fromEntries(
            Object.entries(filterParams).filter(([, value]) => value !== '' && value !== 0 && value != null),
        );
        getMapPoints({data: {businessId, ...data}});
    }, []);

    // A pin says which place it is and leads to its page. It used to be a bare coordinate: zoomed in,
    // the map showed where the locations were and nothing about them.
    const marks = useMemo(
        () => (mapPoints.data ?? []).map((point) => ({
            lat: point.latitude,
            lng: point.longitude,
            title: point.name,
            link: {
                href: AppRouteSpace([AppRouteEnum.app, AppRouteEnum.locations, String(point.id)]),
                label: intl.formatMessage({id: 'locationDetails'}),
            },
        })),
        [mapPoints.data, intl],
    );

    return (
        // The card is the first thing on the page, so it carries the clearance the page header used
        // to: without it it sits directly under the app bar and its notification bell.
        <Box sx={rowSx}>
            {/* Framed and inset — the same treatment the single location page gives its own map, so
                the two read as the same object at two sizes. */}
            <Box sx={mapCardSx}>
                <Box sx={mapTileSx}>
                    <MapWrapper
                        zoom={MapZoomEnum.MIN}
                        markers={marks}
                        clusterization
                    />
                </Box>
            </Box>
        </Box>
    );
};
